import React, { useState, useEffect } from 'react';
import { Tag, X, Plus, Loader2, Search } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from './ui/input';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Shared cache so we don't refetch the catalog on every mount of every row.
let _catalogCache = null;
let _catalogPromise = null;
async function loadCatalog() {
  if (_catalogCache) return _catalogCache;
  if (!_catalogPromise) {
    _catalogPromise = axios.get(`${API}/admin/tag-catalog`, { headers: authHeaders() })
      .then(res => { _catalogCache = res.data || []; return _catalogCache; })
      .catch(() => []);
  }
  return _catalogPromise;
}


// ============================================================================
// TagsEditor — admin control for a customer's tags.
// Shows current tag chips with remove buttons, a free-text add input, and a
// grouped catalog (Species / Group / Workflow / Sample codes) of suggested
// tags so admins can quick-tag customers without typing.
// Sample codes are hydrated from live product SKUs so the picker stays in
// sync with the store.
// ============================================================================

export const TagsEditor = ({ customerId, initialTags = [], onChange, dense = false }) => {
  const [tags, setTags] = useState(initialTags);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [search, setSearch] = useState('');
  const [openCategory, setOpenCategory] = useState(null);

  useEffect(() => { loadCatalog().then(setCatalog); }, []);

  const persist = async (next) => {
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/admin/customers/${customerId}/tags`,
        { tags: next },
        { headers: authHeaders() },
      );
      setTags(res.data.tags);
      onChange?.(res.data.tags);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save tags');
    } finally {
      setBusy(false);
    }
  };

  const addTag = (raw) => {
    const t = (raw ?? input).trim();
    if (!t) return;
    if (tags.some(x => x.toLowerCase() === t.toLowerCase())) {
      setInput('');
      return;
    }
    setInput('');
    persist([...tags, t]);
  };

  const removeTag = (t) => persist(tags.filter(x => x !== t));

  // Suggested tags = catalog minus what's already applied, filtered by search
  const searchLower = search.toLowerCase().trim();
  const catalogFiltered = catalog
    .map(group => ({
      ...group,
      tags: group.tags.filter(t =>
        !tags.some(existing => existing.toLowerCase() === t.tag.toLowerCase()) &&
        (!searchLower || t.tag.toLowerCase().includes(searchLower) || (t.label || '').toLowerCase().includes(searchLower))
      ),
    }))
    .filter(group => group.tags.length > 0);

  return (
    <div data-testid={`tags-editor-${customerId}`}>
      {/* Current tags */}
      <div className="flex flex-wrap gap-1.5 mb-2">
        {tags.map(t => (
          <span
            key={t}
            className="inline-flex items-center gap-1 text-xs bg-[#1f6d62] text-white font-bold px-2 py-1 rounded-full"
          >
            {t}
            <button
              type="button"
              onClick={() => removeTag(t)}
              className="hover:text-red-200"
              data-testid={`tag-remove-${customerId}-${t}`}
              title="Remove tag"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        {tags.length === 0 && <span className="text-xs text-gray-400 italic">No tags yet</span>}
      </div>

      {/* Free-text add */}
      <div className="flex gap-2 mb-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
          placeholder="Add a tag and press Enter"
          className="flex-1"
          disabled={busy}
          data-testid={`tag-input-${customerId}`}
        />
        <Button onClick={() => addTag()} size="sm" disabled={busy} data-testid={`tag-add-${customerId}`}>
          <Plus className="w-4 h-4" />
        </Button>
      </div>

      {/* Grouped catalog suggestions */}
      {catalog.length > 0 && (
        <div className={`mt-2 border-t ${dense ? 'border-gray-100' : 'border-gray-200'} pt-2`}>
          {/* Search box — only worth showing when there are many codes */}
          <div className="relative mb-2">
            <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search suggestions (e.g. sample code)"
              className="pl-8 text-xs h-8"
              data-testid={`tag-suggest-search-${customerId}`}
            />
          </div>

          {catalogFiltered.map(group => {
            const isSampleCodes = group.category === 'Sample codes';
            const isOpen = isSampleCodes ? openCategory === group.category || !!searchLower : true;
            return (
              <div key={group.category} className="mb-2">
                <button
                  type="button"
                  onClick={() => isSampleCodes && setOpenCategory(prev => prev === group.category ? null : group.category)}
                  disabled={!isSampleCodes}
                  className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1 ${
                    isSampleCodes ? 'hover:text-[#1f6d62] cursor-pointer' : 'cursor-default'
                  }`}
                  data-testid={`tag-group-toggle-${customerId}-${group.category.replace(/\s+/g, '-')}`}
                >
                  <Tag className="w-3 h-3" /> {group.category}
                  {isSampleCodes && (
                    <span className="text-gray-400">
                      ({group.tags.length}) {isOpen ? '▾' : '▸'}
                    </span>
                  )}
                </button>
                {isOpen && (
                  <div className="flex flex-wrap gap-1">
                    {(isSampleCodes && !searchLower ? group.tags.slice(0, 60) : group.tags).map(t => (
                      <button
                        key={t.tag}
                        type="button"
                        disabled={busy}
                        onClick={() => addTag(t.tag)}
                        title={t.label}
                        className={`text-[10px] uppercase tracking-widest text-gray-600 hover:text-white hover:bg-[#1f6d62] hover:border-[#1f6d62] font-bold border border-gray-200 rounded-full px-2 py-1 bg-white transition-colors ${
                          isSampleCodes ? 'font-mono' : ''
                        }`}
                        data-testid={`tag-suggest-${customerId}-${t.tag}`}
                      >
                        + {t.tag}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {busy && (
        <div className="flex items-center gap-1.5 text-[10px] text-gray-400 mt-1">
          <Loader2 className="w-3 h-3 animate-spin" /> saving…
        </div>
      )}
    </div>
  );
};

export default TagsEditor;
